import { DayJsRange, VisitListApplyFilter } from '@shared/entity';
import { useVisitListFilters } from '@shared/store';
import {
  getVistsRows,
  VisitRow,
  VisitRowHeader,
  VisitRowPlaceholder,
} from '@features/visits/entity';
import { useVisitsQuery } from '@features/visits/queries';
import { getWorkScheduleMonth } from '@features/profile/work-schedule/api';
import { useIntlTranslations } from '@libs/intl/intl.hooks';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQueries } from '@tanstack/react-query';
import { getMonthsInRange, mergeWorkSchedules } from '@features/visits/schedule/entity';

export type QuickDateFilter = 'today' | 'next7days';
export type VisitsRowData = VisitRow | VisitRowHeader | VisitRowPlaceholder;

export const useVisitRows = (type: VisitListApplyFilter) => {
  const { t } = useIntlTranslations();
  const [visitListFilters, updateVisitFilters] = useVisitListFilters(type);

  const from = dayjs(visitListFilters.dates.from);
  const to = dayjs(visitListFilters.dates.to);

  const { data: visitData } = useVisitsQuery({ query: { from, to } });

  const months = useMemo(() => getMonthsInRange(from, to), [from.valueOf(), to.valueOf()]);

  const workSchedule = useQueries({
    queries: months.map(({ year, month }) => ({
      queryKey: ['workSchedule', year, month],
      queryFn: () => getWorkScheduleMonth(year, month),
      enabled: !isNaN(year) && !isNaN(month),
      retry: false,
    })),
    combine: results => mergeWorkSchedules(results.map(result => result.data)),
  });

  const visitsRows = useMemo(
    () =>
      getVistsRows(visitData, visitListFilters.view, t, {
        workSchedule,
        dateRange: { from, to },
      }),
    [visitData, visitListFilters.view, t, workSchedule, from, to],
  );

  return { rows: visitsRows, visitListFilters, updateVisitFilters };
};

export const QUICK_DATE_FILTERS: Record<
  QuickDateFilter,
  { label: string; from: () => dayjs.Dayjs; to: () => dayjs.Dayjs }
> = {
  today: {
    label: 'Dnes',
    from: () => dayjs().startOf('day'),
    to: () => dayjs().endOf('day'),
  },
  next7days: {
    label: 'Následujících 7 dní',
    from: () => dayjs().startOf('day'),
    to: () => dayjs().add(7, 'day').endOf('day'),
  },
};

export const useQuickDateFilter = (
  updateVisitFilters: ReturnType<typeof useVisitListFilters>[1],
  setValue: ReturnType<typeof useForm<Partial<DayJsRange>>>['setValue'],
) => {
  const [activeFilter, setActiveFilter] = useState<QuickDateFilter | null>(null);

  const apply = (key: QuickDateFilter) => {
    const from = QUICK_DATE_FILTERS[key].from();
    const to = QUICK_DATE_FILTERS[key].to();
    updateVisitFilters?.(draft => {
      draft.dates.from = from.toISOString();
      draft.dates.to = to.toISOString();
    });
    setValue('from', from);
    setValue('to', to);
    setActiveFilter(key);
  };

  return { activeFilter, apply };
};
